
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { debounce, get } from "lodash";
import { CancelTokenSource } from "axios";

import { getDebouncePeriod } from "@/utils/debounce-helper";
import { getNewCancelTokenSource } from "@/utils/cancellableQueryHelper";
import {
	CancellableQuery,
	PaginatedSearchQueryParams,
	VueBootstrapField
} from "@/store/types";
import { ServerType } from "@/store/devices/types";
import SureViewIcon from "@/components/SureViewIcon.vue";
import ConfirmationModal from "@/components-shared/ConfirmationModal.vue";

const Devices = namespace("devices");

@Component({
	components: {
		"sureview-icon": SureViewIcon,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"confirmation-modal": ConfirmationModal
	}
})
export default class DeviceTypeSettings extends Vue {
	@Devices.Action private getDeviceTypes: () => Promise<void>;
	@Devices.Action private getFilteredDeviceTypesPaginated: (
		params: CancellableQuery<PaginatedSearchQueryParams>
	) => Promise<void>;
	@Devices.Action private updateServerType: (serverType: ServerType) => Promise<void>;

	@Devices.State private serverTypes: ServerType[];

	private get getFilteredServerTypes(): ServerType[] {
		return this.serverTypes.filter(f => f.title.toLowerCase().includes(this.searchTerm.toLowerCase()));
	}

	private axiosCancelTokenSource: CancelTokenSource = null;
	private recordsPage: number = 1;
	private recordsPageSize: number = 100;
    private searchTerm: string = "";
    private searchTermPersisted: string = "";
    private tableIsBusy: boolean = false;
    private loadingMoreRecords: boolean = false;
    private allRecordsInCurrentQueryAreRetrieved: boolean = true;
    private filteredRecordsPage: number = 1;
	private currentServerType: ServerType | null = null;
	private isUpdateConfirmationShown: boolean = false;

	private fields: VueBootstrapField[] = [
		{ key: "title", label: "Title", sortable: true },
		{ key: "hidden", label: "Visibility", sortable: true, class: "text-center" }
	];

	private async created(): Promise<void> {
		await Promise.all([
			this.performSearch(this.searchTerm)
		]);
	}

    private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
		this.allRecordsInCurrentQueryAreRetrieved = false;
		await this.performSearch(searchTerm);
	}, getDebouncePeriod());

	private async performSearch(searchTerm: string): Promise<void> {
		this.searchTerm = searchTerm;
		this.tableIsBusy = true;

		this.axiosCancelTokenSource = getNewCancelTokenSource(this.axiosCancelTokenSource, true);

		const cancellableQuery: CancellableQuery<PaginatedSearchQueryParams> = {
			params: { page: this.recordsPage, pageSize: this.recordsPageSize },
			cancelTokenSource: this.axiosCancelTokenSource
		};

		if (this.searchTerm) {
			cancellableQuery.params.page = this.filteredRecordsPage;
			cancellableQuery.params.searchTerm = this.searchTerm;
		}

		await this.getFilteredDeviceTypesPaginated(cancellableQuery);

		if (this.recordsPageSize > this.serverTypes.length) {
				this.allRecordsInCurrentQueryAreRetrieved = true;
		}

		this.tableIsBusy = false;
	}

    private async handleScrollBarReachEnd(): Promise<void> {
		if (this.tableIsBusy || this.allRecordsInCurrentQueryAreRetrieved || this.loadingMoreRecords) {
			return;
		}

		this.loadingMoreRecords = true;
		this.searchTerm ? this.filteredRecordsPage++ : this.recordsPage++;

		await this.performSearch(this.searchTerm);

		this.loadingMoreRecords = false;
	}

	private handleEditClick(ServerType: ServerType) {
		this.currentServerType = {...ServerType};
		this.isUpdateConfirmationShown = true;
	}

	private async handleEditConfirmed(): Promise<void> {
		this.currentServerType.hidden = !this.currentServerType.hidden;
		await this.updateServerType(this.currentServerType);
		this.isUpdateConfirmationShown = false;
	}

	private handleEditCancelled() {
		this.isUpdateConfirmationShown = false;
	}

	private getServerTypeTitle(serverType: ServerType) : string {
		return serverType?.title;
	}

	private getServerTypeToggledVisibility(serverType: ServerType) : string {
		return serverType?.hidden ? 'Visible' : 'Hidden';
	}
}
